import { teamMembersSchema, type TeamMember } from '@houzy/schemas/partner-cockpit/user-management';
import {
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiRequest } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';
import { STALE_TIME_MS } from '../../../lib/queryClient';

export type { TeamMember };

export async function fetchTeamMembers(signal?: AbortSignal): Promise<TeamMember[]> {
    return apiRequest(partnerCockpitApiPath('team/members'), { signal, responseSchema: teamMembersSchema });
}

export const teamMembersQueryKey = ['settings', 'teamMembers'] as const;

export const teamMembersQueryOptions = queryOptions({
    queryKey: teamMembersQueryKey,
    queryFn: ({ signal }) => fetchTeamMembers(signal),
    staleTime: STALE_TIME_MS,
});

export function useTeamMembers(): UseQueryResult<TeamMember[]> {
    return useQuery(teamMembersQueryOptions);
}

export function useInviteMember(): UseMutationResult<void, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['settings', 'team', 'invite'],
        mutationFn: (email: string) =>
            apiRequest(partnerCockpitApiPath('team/members/invite'), { method: 'POST', body: { email } }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamMembersQueryKey }).catch(() => undefined);
        },
    });
}

export function useRemoveMember(): UseMutationResult<void, Error, string> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['settings', 'team', 'remove'],
        mutationFn: (membershipUuid: string) =>
            apiRequest(partnerCockpitApiPath(`team/members/${membershipUuid}`), { method: 'DELETE' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: teamMembersQueryKey }).catch(() => undefined);
        },
    });
}
